import { useEffect, useId, useMemo, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { padlockSvg } from '../art/padlockSvg'
import { SEAL_COLORS, SealTone } from '../art/sealSvg'
import { MONO } from '../ui'

/*
 * The seal of a lock, drawn as the CADINU Lock padlock (art/padlockSvg.ts; round 2 of the redesign, Bez 2026-10-09). One object for
 * every surface: the landing hero (decorative), the create header and review, the certificate, the list rows and the success view.
 *
 * One authored motion, once per mount with `animate`: the shackle drops home with a small overshoot ("click"), the seam light flashes and
 * the ring of time served sweeps to its value (about 1.2 s). It never replays when the countdown ticks (the drawing only changes when the
 * progress moves by 0.1 %), and under prefers-reduced-motion the padlock is drawn at rest.
 */

const drop = keyframes`
  0% { transform: translateY(-22px); }
  55% { transform: translateY(0); }
  70% { transform: translateY(-3px); }
  100% { transform: translateY(0); }
`
const flash = keyframes`
  0%, 50% { opacity: 0.15; }
  62% { opacity: 1; filter: brightness(1.8); }
  100% { opacity: 1; filter: none; }
`
const sweep = keyframes`
  from { stroke-dasharray: 0 400; }
`
const motion = css`
  .shackle {
    animation: ${drop} 0.7s cubic-bezier(0.3, 0, 0.2, 1) 0.15s both;
  }
  .seam {
    animation: ${flash} 1s ease-out 0.15s both;
  }
  .arc {
    animation: ${sweep} 1.1s cubic-bezier(0.16, 1, 0.3, 1) 0.5s both;
  }
`
const Wrap = styled.div<{ $mini?: boolean; $play: boolean }>`
  position: relative;
  width: 100%;
  line-height: 0;
  svg {
    display: block;
    width: 100%;
    height: auto;
    aspect-ratio: 200 / 220;
    overflow: visible;
  }
  ${({ $mini }) =>
    $mini
      ? css`
          height: 100%;
          svg {
            width: auto;
            height: 100%;
            margin: 0 auto;
          }
        `
      : ''}
  ${({ $play }) => ($play ? motion : '')}
  @media (prefers-reduced-motion: reduce) {
    .shackle,
    .seam,
    .arc {
      animation: none;
    }
  }
`
/** The time left, under the padlock: HTML in the page's mono face, never laid over the drawing. */
const Label = styled.div<{ $color: string }>`
  display: flex;
  justify-content: center;
  margin-top: 8px;
  line-height: 18px;
  span {
    padding: 3px 12px;
    border-radius: 999px;
    white-space: nowrap;
    font-family: ${MONO};
    font-size: 13px;
    font-weight: 500;
    color: #dfe9ff;
    background: rgba(5, 7, 19, 0.78);
    border: 1px solid ${({ $color }) => $color};
  }
`

export default function Seal({
  progress,
  tone,
  label,
  mini,
  animate,
  decorative,
  className,
  testid,
}: {
  progress: number
  tone: SealTone
  label?: string
  mini?: boolean
  animate?: boolean
  /** an illustration (the landing hero, the create header): hidden from assistive technology */
  decorative?: boolean
  className?: string
  testid?: string
}) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '')
  const [play, setPlay] = useState(Boolean(animate))
  useEffect(() => {
    if (!animate) return undefined
    const t = setTimeout(() => setPlay(false), 1800)
    return () => clearTimeout(t)
  }, [animate])
  // the drawing changes only when the progress moves by 0.1 %, not on every tick of the countdown
  const p = Math.round(Math.min(1, Math.max(0, Number.isFinite(progress) ? progress : 0)) * 1000) / 1000
  const svg = useMemo(() => padlockSvg({ progress: p, tone, uid: `s${uid}`, mini }), [p, tone, uid, mini])
  const a11y = decorative
    ? { 'aria-hidden': true as const }
    : {
        role: 'img',
        'aria-label': label
          ? `Lock seal, ${label}`
          : tone === 'released'
          ? 'Open padlock: released'
          : 'Closed padlock: locked',
      }
  return (
    <Wrap
      $mini={mini}
      $play={play}
      className={className}
      data-testid={testid ?? 'seal'}
      data-progress={progress.toFixed(3)}
      data-tone={tone}
      {...a11y}
    >
      {/* the markup is built from numbers and fixed colours only (art/padlockSvg.ts), never from a lock's text */}
      <span dangerouslySetInnerHTML={{ __html: svg }} style={{ display: 'contents' }} />
      {label && !mini ? (
        <Label $color={SEAL_COLORS[tone].lit}>
          <span>{label}</span>
        </Label>
      ) : null}
    </Wrap>
  )
}
